/* AddInstructionsRenderer.js */

"use strict";
const escapeHtml = require("@gluenetworks/dom_utilities").escapeHtml;
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const InputRadioControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .InputRadio;
const InputTextControl = require("@gluenetworks/steve_ui_core").Control.Textfield.InputText;
const TextareaControl = require("@gluenetworks/steve_ui_core").Control.Textfield.Textarea;
const MultipleComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection
    .Multiple.ComboBox;
const InputFileControl = require("@gluenetworks/steve_ui_core").Control.Action.InputFile;
const FancyLoadingMask = require("@gluenetworks/fancy_loading_mask_gl");

const VALID_FILE_EXTENSIONS = [".pdf", ".docx"];
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const DROPAREA_TEXT = "Drop Instructions File to Upload";

class AddInstructionsRenderer {
    constructor(model, selectedPlatforms, isEditMode = false, instructionToEdit = null) {
        this._model = model;
        this._selectedPlatforms = selectedPlatforms || [];
        this._isEditMode = isEditMode;
        this._instructionToEdit = instructionToEdit;

        // Root element for the veil
        this._rootElem = document.createElement("platform-add-instructions-gl");
        this._rootElem.component = this._model;

        this._titleComponent = document.createElement("h2");
        this._titleComponent.innerText = isEditMode ? "Edit Instructions" : "Add Instructions";

        this._contentContainer = document.createElement("content-container");
        this._modeSelectionContainer = document.createElement("mode-selection-container");

        // In edit mode, hide "Select existing" option and pre-select based on content type
        const radioOptions = isEditMode
            ? [
                  {
                      label: "Add Instructions from a Text",
                      value: "create-text",
                  },
                  {
                      label: "Add Instructions from a File",
                      value: "create-file",
                  },
              ]
            : [
                  {
                      label: "Add Instructions from a Text",
                      value: "create-text",
                  },
                  {
                      label: "Add Instructions from a File",
                      value: "create-file",
                  },
              ];

        // Only show "Select existing" option if there are existing instructions (non-edit mode only)
        if (!isEditMode && this._model.getInstructionOptions().length > 0) {
            radioOptions.push({
                label: "Select existing Instructions",
                value: "select",
            });
        }

        // Determine initial value based on edit mode
        let initialValue = "create-text";
        if (isEditMode && instructionToEdit) {
            const contentType = instructionToEdit.getContentType();
            initialValue = contentType === "TEXT" ? "create-text" : "create-file";
            if (initialValue === "create-file") {
                this._titleComponent.innerText = "Upload Instructions";
            }
        }

        this._modeRadio = new InputRadioControl({
            darkTheme: true,
            horizontal: false,
            options: radioOptions,
            value: initialValue,
            visible: !isEditMode,
        });
        this._currentMode = initialValue;

        this._formTextSection = document.createElement("form-text-section");

        this._nameField = new InputTextControl({
            darkTheme: true,
            value: "",
        });

        this._instructionsField = new TextareaControl({
            darkTheme: true,
            value: "",
            rows: 10,
        });

        this._formFileSection = document.createElement("form-file-section");
        this._formFileSection.style.display = "none";
        this._fileDropArea = document.createElement("file-drop-area");
        this._fileDropArea.setAttribute("draggable", true);

        this._dropAreaContent = document.createElement("drop-area-content");
        this._plusIcon = document.createElement("plus-icon");
        this._plusIcon.innerHTML = "+";
        this._dropAreaText = document.createElement("drop-area-text");
        this._dropAreaText.innerText = DROPAREA_TEXT;
        this._fileInputControl = new InputFileControl({
            id: "platformAddInstructionsFileUpload",
            label: "Upload Instructions",
            acceptFiles: ".pdf, .docx",
            darkTheme: true,
            multiple: false,
        });

        this._uploadProgressContainer = document.createElement("upload-progress-container");
        this._uploadProgressContainer.style.display = "none";

        this._loadingMask = new FancyLoadingMask({
            darkTheme: true,
            shown: false,
        });
        this._uploadProgressContainer.appendChild(this._loadingMask.renderer.rootElement);
        this._uploadButton = new ButtonControl({
            label: "Upload Instructions",
            darkTheme: true,
            theme: "additive",
        });
        this._uploadButton.renderer.rootElement.style.display = "none";

        this._cancelUploadButton = new ButtonControl({
            label: "Cancel Upload",
            darkTheme: true,
        });
        this._cancelUploadButton.renderer.rootElement.style.display = "none";

        this._selectedFile = null;
        this._isUploading = false;

        this._browseRow = document.createElement("browse-row");
        this._browseRow.style.display = "none";

        this._errorContainer = document.createElement("error-container");
        this._errorContainer.style.display = "none";
        this._selectExistingSection = document.createElement("select-existing-section");
        this._selectExistingSection.style.display = "none";

        this._existingInstructionsComboBox = new MultipleComboBoxControl({
            darkTheme: true,
            placeholder: "Search and select instructions...",
            options: this._model.getInstructionOptions(),
        });
        this._buttonContainer = document.createElement("button-container");

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            darkTheme: true,
        });

        this._saveButton = new ButtonControl({
            label: "Save",
            darkTheme: true,
            theme: "active",
        });

        // Bind all event handlers in constructor
        this._onSaveButtonClick = this._onSaveButtonClick.bind(this);
        this._onUploadButtonClick = this._onUploadButtonClick.bind(this);
        this._onCancelUploadButtonClick = this._onCancelUploadButtonClick.bind(this);
        this._onCancelButtonClick = this._onCancelButtonClick.bind(this);
        this._onModeChange = this._onModeChange.bind(this);
        this._onFileSelected = this._onFileSelected.bind(this);
        this._onShowError = this._onShowError.bind(this);
        this._onUploadProgress = this._onUploadProgress.bind(this);
        this._onFileDrop = this._onFileDrop.bind(this);
        this._onDragOver = this._onDragOver.bind(this);
        this._onDragLeave = this._onDragLeave.bind(this);
        this._boundValidateForm = this._validateForm.bind(this);

        this._registerListeners();
    }

    _registerListeners() {
        this._saveButton.on("UserClick", this._onSaveButtonClick);
        this._uploadButton.on("UserClick", this._onUploadButtonClick);
        this._cancelUploadButton.on("UserClick", this._onCancelUploadButtonClick);
        this._cancelButton.on("UserClick", this._onCancelButtonClick);
        this._modeRadio.on("UserValue", this._onModeChange);
        this._nameField.on("UserValue", this._boundValidateForm);
        this._instructionsField.on("UserValue", this._boundValidateForm);
        this._fileInputControl.on("FileHandle", this._onFileSelected);
        this._existingInstructionsComboBox.on("UserValue", this._boundValidateForm);
        this._model.on("ShowError", this._onShowError);
        this._model.on("UploadProgress", this._onUploadProgress);
        this._fileDropArea.addEventListener("drop", this._onFileDrop);
        this._fileDropArea.addEventListener("dragover", this._onDragOver);
        this._fileDropArea.addEventListener("dragleave", this._onDragLeave);
    }

    _unregisterListeners() {
        this._saveButton.removeAllListeners("UserClick");
        this._uploadButton.removeAllListeners("UserClick");
        this._cancelUploadButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        this._modeRadio.removeAllListeners("UserValue");
        this._nameField.removeAllListeners("UserValue");
        this._instructionsField.removeAllListeners("UserValue");
        this._fileInputControl.removeAllListeners("FileHandle");
        this._existingInstructionsComboBox.removeAllListeners("UserValue");
        this._model.removeListener("ShowError", this._onShowError);
        this._model.removeListener("UploadProgress", this._onUploadProgress);
        this._fileDropArea.removeEventListener("drop", this._onFileDrop);
        this._fileDropArea.removeEventListener("dragover", this._onDragOver);
        this._fileDropArea.removeEventListener("dragleave", this._onDragLeave);
    }

    _onCancelButtonClick() {
        this._model.closeAction();
    }

    _onSaveButtonClick() {
        if (this._currentMode === "create-text") {
            const formData = {
                name: this._nameField.getValue(),
                contents: this._instructionsField.getValue(),
            };

            this._model.createInstructionFromText(formData);
        } else if (this._currentMode === "select") {
            const selectedInstructions = this._existingInstructionsComboBox.getValue();
            this._model.linkExistingInstructions(selectedInstructions);
        }
    }

    _onFileSelected(event) {
        event.preventDefault();
        const files = event.target.files;
        if (files && files.length > 0) {
            this._handleFileSelection(files[0]);
        }
    }

    _onFileDrop(event) {
        event.preventDefault();
        this._fileDropArea.classList.remove("drag-over");

        const files = event.dataTransfer.files;
        if (files && files.length > 0) {
            this._handleFileSelection(files[0]);
        }
    }

    _onDragOver(event) {
        event.preventDefault();
        this._fileDropArea.classList.add("drag-over");
    }

    _onDragLeave(event) {
        event.preventDefault();
        this._fileDropArea.classList.remove("drag-over");
    }

    _handleFileSelection(file) {
        // Validate file type against allowed extensions
        const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
        if (!VALID_FILE_EXTENSIONS.includes(fileExtension)) {
            this._showError("Invalid file type. Only PDF and DOCX files are allowed.");
            return;
        }

        this._selectedFile = file;
        this._dropAreaText.innerHTML = `Selected: ${escapeHtml(
            file.name
        )}<br/><span class="file-size">(${this._formatFileSize(file.size)})</span>`;
        this._hideError();
        this._startUpload();
    }

    _formatFileSize(bytes) {
        if (bytes < 1024) {
            return bytes + " B";
        }
        if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(2) + " KB";
        }
        return (bytes / (1024 * 1024)).toFixed(2) + " MB";
    }

    _onUploadButtonClick() {
        if (!this._selectedFile) {
            this._showError("Please select a file to upload.");
            return;
        }

        this._startUpload();
    }

    _startUpload() {
        if (!this._selectedFile) {
            this._showError("Please select a file to upload.");
            return;
        }

        this._isUploading = true;
        this._uploadButton.renderer.rootElement.style.display = "none";
        this._cancelUploadButton.renderer.rootElement.style.display = "";
        this._cancelUploadButton.setDisabled(false);
        this._uploadProgressContainer.style.display = "block";
        this._loadingMask.show();
        this._fileDropArea.style.display = "none";
        this._browseRow.style.display = "none";

        this._model.uploadInstructionFile(this._selectedFile);
    }

    _onCancelUploadButtonClick() {
        this._model.abortUpload();
        this._resetUploadUI();
        this._browseRow.style.display = "";
    }

    _onUploadProgress(bytesUploaded, bytesTotal, percentage) {
        this._loadingMask.showProgress(`Uploading... ${percentage.toFixed(1)}%`);
    }

    _resetUploadUI() {
        if (this._isUploading) {
            this._loadingMask.hide();
        }
        this._isUploading = false;
        this._selectedFile = null;
        this._uploadButton.renderer.rootElement.style.display = "none";
        this._cancelUploadButton.renderer.rootElement.style.display = "none";
        this._uploadProgressContainer.style.display = "none";
        this._loadingMask.showProgress("Uploading... 0%");
        this._fileDropArea.style.display = "";
        this._fileInputControl.setDisabled(false);
        this._fileInputControl.setValue("");
        this._fileDropArea.classList.remove("uploading");
        this._dropAreaText.innerText = DROPAREA_TEXT;
        this._model.resetUploadState();
    }

    _onModeChange(val) {
        const newMode = val;
        this._currentMode = newMode;

        if (newMode === "create-text") {
            this._formTextSection.style.display = "";
            this._formFileSection.style.display = "none";
            this._selectExistingSection.style.display = "none";
            this._saveButton.renderer.rootElement.style.display = "";
            this._uploadButton.renderer.rootElement.style.display = "none";
            this._cancelButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "none";
            this._resetUploadUI();
        } else if (newMode === "create-file") {
            this._formTextSection.style.display = "none";
            this._formFileSection.style.display = "";
            this._selectExistingSection.style.display = "none";
            this._saveButton.renderer.rootElement.style.display = "none";
            this._uploadButton.renderer.rootElement.style.display = "";
            this._cancelButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "";
            this._resetUploadUI();
        } else {
            this._formTextSection.style.display = "none";
            this._formFileSection.style.display = "none";
            this._selectExistingSection.style.display = "";
            this._saveButton.renderer.rootElement.style.display = "";
            this._uploadButton.renderer.rootElement.style.display = "none";
            this._cancelButton.renderer.rootElement.style.display = "";
            this._browseRow.style.display = "none";
            this._resetUploadUI();
        }

        this._hideError();
        this._validateForm();
    }

    _validateForm() {
        const isValid = this._isFormValid();

        if (this._currentMode === "create-file") {
            this._uploadButton.setDisabled(!isValid);
        } else {
            this._saveButton.setDisabled(!isValid);
        }
    }

    _isFormValid() {
        if (this._currentMode === "create-text") {
            const name = this._nameField.getValue();
            const instructions = this._instructionsField.getValue();
            if (!name || name.trim().length === 0) {
                return false;
            }

            if (!instructions || instructions.trim().length === 0) {
                return false;
            }

            return true;
        } else if (this._currentMode === "create-file") {
            return this._selectedFile !== null;
        } else {
            const selected = this._existingInstructionsComboBox.getValue();
            return selected && selected.length > 0;
        }
    }

    showError(message) {
        this._errorContainer.innerText = message;
        this._errorContainer.style.display = "block";
    }

    _hideError() {
        this._errorContainer.style.display = "none";
    }

    _onShowError(message) {
        this.showError(message);
    }

    _showError(message) {
        this.showError(message);
    }

    shutdown() {
        this._unregisterListeners();
        let promArr = [
            this._saveButton.shutdown(),
            this._uploadButton.shutdown(),
            this._cancelUploadButton.shutdown(),
            this._cancelButton.shutdown(),
            this._modeRadio.shutdown(),
            this._nameField.shutdown(),
            this._instructionsField.shutdown(),
            this._fileInputControl.shutdown(),
            this._existingInstructionsComboBox.shutdown(),
            this._loadingMask.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                this._saveButton = undefined;
                this._uploadButton = undefined;
                this._cancelUploadButton = undefined;
                this._cancelButton = undefined;
                this._modeRadio = undefined;
                this._nameField = undefined;
                this._instructionsField = undefined;
                this._fileInputControl = undefined;
                this._existingInstructionsComboBox = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._modeSelectionContainer.remove();
                this._modeSelectionContainer = undefined;
                this._formTextSection.remove();
                this._formTextSection = undefined;
                this._fileDropArea.remove();
                this._fileDropArea = undefined;
                this._dropAreaContent.remove();
                this._dropAreaContent = undefined;
                this._plusIcon.remove();
                this._plusIcon = undefined;
                this._dropAreaText.remove();
                this._dropAreaText = undefined;
                this._browseRow.remove();
                this._browseRow = undefined;
                this._uploadProgressContainer.remove();
                this._uploadProgressContainer = undefined;
                this._loadingMask = undefined;
                this._formFileSection.remove();
                this._formFileSection = undefined;
                this._selectExistingSection.remove();
                this._selectExistingSection = undefined;
                this._buttonContainer.remove();
                this._buttonContainer = undefined;
                this._contentContainer.remove();
                this._contentContainer = undefined;
                this._titleComponent.remove();
                this._titleComponent = undefined;
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._model.logger.warn(err, "AddInstructionsRenderer.shutdown() error");
            }
        );
    }

    render() {
        return new Promise((resolve) => {
            if (!this._rootElem.hasChildNodes()) {
                this._contentContainer.appendChild(this._titleComponent);

                // Always show mode selection (with filtered options in edit mode)
                this._modeSelectionContainer.appendChild(this._modeRadio.renderer.rootElement);
                this._contentContainer.appendChild(this._modeSelectionContainer);

                // Disable mode selection in edit mode
                if (this._isEditMode) {
                    this._modeRadio.setDisabled(true);
                }

                const createFieldRow = (labelText, field) => {
                    const row = document.createElement("field-row");
                    const labelElem = document.createElement("label");
                    labelElem.innerText = labelText + ":";
                    row.appendChild(labelElem);
                    row.appendChild(field.renderer.rootElement);
                    return row;
                };

                // Pre-populate fields in edit mode
                if (this._isEditMode && this._instructionToEdit) {
                    if (this._currentMode === "create-text") {
                        // Pre-populate text fields
                        this._nameField.setValue(this._instructionToEdit.getFilename() || "");
                        this._instructionsField.setValue(
                            this._instructionToEdit.getContents() || ""
                        );
                    }
                }

                this._formTextSection.appendChild(createFieldRow("Name", this._nameField));
                this._formTextSection.appendChild(
                    createFieldRow("Instructions", this._instructionsField)
                );
                this._contentContainer.appendChild(this._formTextSection);
                this._dropAreaContent.appendChild(this._plusIcon);
                this._dropAreaContent.appendChild(this._dropAreaText);
                this._fileDropArea.appendChild(this._dropAreaContent);
                this._formFileSection.appendChild(this._fileDropArea);
                this._browseRow.appendChild(this._fileInputControl.renderer.rootElement);
                this._buttonContainer.appendChild(this._browseRow);
                this._contentContainer.appendChild(this._formFileSection);

                // Always add "Select existing" section (will be hidden via display style in edit mode)
                const comboBoxRow = document.createElement("field-row");
                const comboBoxLabel = document.createElement("label");
                comboBoxLabel.innerText = "Select Instructions:";
                comboBoxRow.appendChild(comboBoxLabel);
                comboBoxRow.appendChild(this._existingInstructionsComboBox.renderer.rootElement);
                this._selectExistingSection.appendChild(comboBoxRow);
                this._contentContainer.appendChild(this._selectExistingSection);

                this._rootElem.appendChild(this._contentContainer);

                this._contentContainer.appendChild(this._errorContainer);
                this._contentContainer.appendChild(this._uploadProgressContainer);
                this._buttonContainer.appendChild(this._cancelUploadButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._cancelButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._saveButton.renderer.rootElement);
                this._buttonContainer.appendChild(this._uploadButton.renderer.rootElement);
                this._contentContainer.appendChild(this._buttonContainer);

                // Set initial form display based on mode
                this._onModeChange(this._currentMode);

                this._validateForm();
            }
            resolve();
        });
    }

    get rootElement() {
        return this._rootElem;
    }

    getRootElement() {
        return this.rootElement;
    }

    get model() {
        return this._model;
    }

    getModel() {
        return this.model;
    }
}

module.exports = AddInstructionsRenderer;
